import { create } from "zustand";
import { EquipmentItem } from "@/backend/models/equipmentItem";
import { nativeBridge } from "../embed/nativeBridge";
import {
  DesktopConnection,
  DesktopConnectionReply,
  DesktopInstrument,
  needsEngineConnection,
} from "@/types/instrumentBridgeTypes";

/**
 * The instruments on the user's machine, and which equipment is connected.
 *
 * None of this is this app's state. Instruments are configured in
 * veriform-desktop and recorded nowhere here; connections live in the workflow
 * engine, scoped by the procedure id. The page asks for both and holds the
 * answers until it is reloaded.
 *
 * Both the assignment and the connected state are desktop's answer, not
 * something this store works out. veriform-desktop records which instrument an
 * equipment item is assigned to, so the choice survives this page reloading;
 * the engine says which of those it is actually holding. The two differ after
 * a desktop restart, where the assignment is remembered and the connection is
 * not.
 */

interface ConnectionsByEquipment {
  [equipmentId: string]: string;
}

export interface InstrumentStore {
  instruments: DesktopInstrument[];
  /** Equipment ids the engine holds a connection for. */
  connectedEquipmentIds: string[];
  instrumentIdByEquipmentId: ConnectionsByEquipment;
  /** The configuration each connection was last given, as desktop stored it. */
  configurationByEquipmentId: ConnectionsByEquipment;
  /** Equipment ids with a connect or disconnect in flight. */
  busyEquipmentIds: string[];
  errorByEquipmentId: Record<string, string>;
  loaded: boolean;
  /** A load in flight, so the many rows that mount together ask only once. */
  loading: boolean;
  /** Why asking desktop for its instruments and connections failed. */
  loadError?: string;

  load: (procedureId: string) => Promise<void>;
  /**
   * Start the provider. Without `instrumentId` it is the one already assigned;
   * without `configuration` it is what was last stored for this equipment.
   */
  connect: (
    equipment: EquipmentItem,
    procedureId: string,
    configuration?: string,
    instrumentId?: string,
  ) => Promise<void>;
  /** Record which instrument an equipment item uses, without starting it. */
  save: (equipment: EquipmentItem, procedureId: string, instrumentId: string, configuration: string) => Promise<void>;
  disconnect: (equipment: EquipmentItem, procedureId: string) => Promise<void>;
  instrumentFor: (equipmentId: string) => DesktopInstrument | undefined;
  isReady: (equipmentId: string) => boolean;
  reset: () => void;
}

const initialState = {
  instruments: [] as DesktopInstrument[],
  connectedEquipmentIds: [] as string[],
  instrumentIdByEquipmentId: {} as ConnectionsByEquipment,
  configurationByEquipmentId: {} as ConnectionsByEquipment,
  busyEquipmentIds: [] as string[],
  errorByEquipmentId: {} as Record<string, string>,
  loaded: false,
  loading: false,
  loadError: undefined as string | undefined,
};

/**
 * The equipment's own defaults, as desktop's connect dialog seeds them from
 * `defaultConfiguration.objectInstance.properties` (ConnectionContent.tsx:51).
 * Only the fallback for a connection that has never been configured.
 */
function connectionConfig(equipment: EquipmentItem): string {
  const properties = equipment.defaultConfiguration?.objectInstance?.properties ?? [];
  return JSON.stringify(
    Object.fromEntries(properties.map(property => [property.key, property.value])),
  );
}

/**
 * Bumped by `reset`, so a load still in flight for the procedure just left does
 * not land on the next one.
 */
let generation = 0;

export const useInstrumentStore = create<InstrumentStore>((set, get) => {
  const setBusy = (equipmentId: string, busy: boolean) => {
    set(state => ({
      busyEquipmentIds: busy
        ? [...new Set([...state.busyEquipmentIds, equipmentId])]
        : state.busyEquipmentIds.filter(id => id !== equipmentId),
    }));
  };

  /**
   * One write for both Confirm and Confirm and connect, so the two cannot drift.
   * `start` is the only difference: false records the choice and leaves the
   * provider alone.
   */
  const addConnection = async (
    equipment: EquipmentItem,
    procedureId: string,
    instrumentId: string,
    configuration: string,
    start: boolean,
  ): Promise<void> => {
    if (get().busyEquipmentIds.includes(equipment.id)) return;
    setBusy(equipment.id, true);

    try {
      const reply = await nativeBridge.call<DesktopConnectionReply>('engine.addConnection', {
        procedureId,
        equipmentId: equipment.id,
        equipmentIdentifier: equipment.identifier,
        instrumentId,
        connectionConfig: configuration,
        start,
      });

      // `started` false is the provider refusing to start, which the engine
      // reports without the call itself failing -- and is also what a save
      // answers, having never asked it to start.
      set(state => ({
        connectedEquipmentIds: reply.started
          ? [...new Set([...state.connectedEquipmentIds, equipment.id])]
          : state.connectedEquipmentIds,
        instrumentIdByEquipmentId: { ...state.instrumentIdByEquipmentId, [equipment.id]: instrumentId },
        configurationByEquipmentId: { ...state.configurationByEquipmentId, [equipment.id]: configuration },
        errorByEquipmentId: { ...state.errorByEquipmentId, [equipment.id]: reply.error ?? '' },
      }));
    } catch (error) {
      set(state => ({
        errorByEquipmentId: { ...state.errorByEquipmentId, [equipment.id]: (error as Error).message },
      }));
    } finally {
      setBusy(equipment.id, false);
    }
  };

  return {
    ...initialState,

    load: async (procedureId: string) => {
      if (get().loading || get().loaded) return;
      set({ loading: true, loadError: undefined });
      const started = generation;

      let instruments: DesktopInstrument[];
      let connections: DesktopConnection[];
      try {
        [instruments, connections] = await Promise.all([
          nativeBridge.call<DesktopInstrument[]>('engine.listInstruments'),
          nativeBridge.call<DesktopConnection[]>('engine.listConnections', { procedureId }),
        ]);
      } catch (error) {
        if (started === generation) set({ loading: false, loadError: (error as Error).message });
        return;
      }
      if (started !== generation) return;

      set({
        loading: false,
        instruments,
        connectedEquipmentIds: connections.filter(c => c.connected).map(c => c.equipmentId),
        // Seeded from desktop rather than left empty, so a reload comes back
        // showing what each equipment item is wired to.
        instrumentIdByEquipmentId: Object.fromEntries(
          connections.map(connection => [connection.equipmentId, connection.instrumentId]),
        ),
        configurationByEquipmentId: Object.fromEntries(
          connections.map(connection => [connection.equipmentId, connection.configuration]),
        ),
        loaded: true,
      });
    },

    save: async (equipment, procedureId, instrumentId, configuration) => {
      set(state => ({
        instrumentIdByEquipmentId: { ...state.instrumentIdByEquipmentId, [equipment.id]: instrumentId },
      }));
      await addConnection(equipment, procedureId, instrumentId, configuration, false);
    },

    connect: async (equipment, procedureId, configuration, instrumentId) => {
      const assignedId = instrumentId ?? get().instrumentIdByEquipmentId[equipment.id];
      if (!assignedId) return;

      if (instrumentId) {
        set(state => ({
          instrumentIdByEquipmentId: { ...state.instrumentIdByEquipmentId, [equipment.id]: instrumentId },
        }));
      }

      await addConnection(
        equipment, procedureId, assignedId,
        // What the dialog was confirmed with, else what was stored last time --
        // desktop's Start uses the stored connection's configuration -- and only
        // then the equipment item's own defaults.
        configuration
          ?? get().configurationByEquipmentId[equipment.id]
          ?? connectionConfig(equipment),
        true,
      );
    },

    disconnect: async (equipment: EquipmentItem, procedureId: string) => {
      if (get().busyEquipmentIds.includes(equipment.id)) return;

      setBusy(equipment.id, true);

      try {
        await nativeBridge.call('engine.removeConnection', {
          procedureId,
          equipmentId: equipment.id,
          equipmentIdentifier: equipment.identifier,
        });
        set(state => ({
          connectedEquipmentIds: state.connectedEquipmentIds.filter(id => id !== equipment.id),
          errorByEquipmentId: { ...state.errorByEquipmentId, [equipment.id]: '' },
        }));
      } catch (error) {
        // Left connected: the engine was not told to drop it, so as far as
        // anything else is concerned it still holds the connection.
        set(state => ({
          errorByEquipmentId: {
            ...state.errorByEquipmentId,
            [equipment.id]: (error as Error).message,
          },
        }));
      } finally {
        setBusy(equipment.id, false);
      }
    },

    instrumentFor: (equipmentId: string) => {
      const instrumentId = get().instrumentIdByEquipmentId[equipmentId];
      return get().instruments.find(instrument => instrument.id === instrumentId);
    },

    /**
     * Whether this equipment is ready for a run. Manual control needs no engine
     * connection, so being assigned one is enough.
     */
    isReady: (equipmentId: string) => {
      const instrument = get().instrumentFor(equipmentId);
      if (instrument && !needsEngineConnection(instrument)) return true;
      return get().connectedEquipmentIds.includes(equipmentId);
    },

    reset: () => {
      generation += 1;
      set({ ...initialState });
    },
  };
});
